import React, { useEffect, useState } from "react";
import Card from "components/card";
import { MdEdit } from "react-icons/md";
import CreateLLMModelModal from "./components/CreateLLMModelModal";
import CreateEmbeddingModelModal from "./components/CreateEmbeddingModelModal";
import EditLLMModelModal from "./components/EditLLMModelModal";
import EditEmbeddingModelModal from "./components/EditEmbeddingModelModal";
import {
  CreateModelRequest,
  EMBEDDING_MODEL_MAPPING,
  globalModelsApi,
  LLM_MODEL_MAPPING,
  Model,
  organizationModelsApi,
  UpdateModelRequest,
} from "api/models";
import { useAuthContext } from "auth/AuthProvider";
import { handleAxiosAndCaughtError } from "utils";
import GenericTable from "components/table";
import { CellContext } from "@tanstack/react-table";
import {
  getEmbeddingDisplayName,
  getLLMDisplayName,
} from "utils/config-model-utils";

import { useTranslation } from "react-i18next";

interface ModelPageProps {
  settingsType: "global" | "organization";
}

const ModelPage: React.FC<ModelPageProps> = ({ settingsType }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [activeTab, setActiveTab] = useState<"llm" | "embedding">("llm");
  const [isLLMCreateModalOpen, setIsLLMCreateModalOpen] = useState(false);
  const [isLLMEditModalOpen, setIsLLMEditModalOpen] = useState(false);
  const [isEmbeddingCreateModalOpen, setIsEmbeddingCreateModalOpen] = useState(false);
  const [isEmbeddingEditModalOpen, setIsEmbeddingEditModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch models on component mount and when tab changes
  useEffect(() => {
    fetchModels();
  }, [settingsType, activeTab]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError(null);

      let modelsData: Model[] = [];

      if (settingsType === "global") {
        if (activeTab === "llm") {
          modelsData = await globalModelsApi.getLLMModels();
        } else {
          modelsData = await globalModelsApi.getEmbeddingModels();
        }
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        if (activeTab === "llm") {
          modelsData = await organizationModelsApi.getLLMModels(organizationId);
        } else {
          modelsData = await organizationModelsApi.getEmbeddingModels(
            organizationId
          );
        }
      }

      setModels(Array.isArray(modelsData) ? modelsData : []);
    } catch (err) {
      handleAxiosAndCaughtError(err);
      setError(t("settings.failedToFetchModels"));
    } finally {
      setLoading(false);
    }
  };

  // No need for filteredModels since GenericTable handles filtering

  // Define columns for GenericTable
  const columns = [
    {
      accessorKey: "name",
      header: () => (
        <p>
          {t("settings.modelName", {
            type:
              activeTab === "llm" ? t("settings.llm") : t("settings.embedding"),
          })}
        </p>
      ),
      cell: (info: CellContext<Model, string>) => (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {info.getValue()}
        </p>
      ),
    },
    {
      accessorKey: "id",
      header: () => <p>{t("settings.action")}</p>,
      cell: (info: CellContext<Model, string>) => {
        const model = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditModel(model)}
              className="flex items-center justify-center rounded-md p-2 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
              title={t("settings.editModel")}
            >
              <MdEdit className="h-5 w-5" />
            </button>
            {/* <button
              onClick={() => handleDeleteModel(model.id)}
              className="flex items-center justify-center rounded-md p-2 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              title="Delete model"
            >
              <MdDelete className="h-5 w-5" />
            </button> */}
          </div>
        );
      },
      meta: {
        cellClassName: "min-w-[100px]",
        headerClassName: "min-w-[100px]",
      },
    },
  ];

  const handleDeleteModel = async (modelId: string) => {
    try {
      if (settingsType === "global") {
        await globalModelsApi.archive(modelId);
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        await organizationModelsApi.archive(organizationId, modelId);
      }

      // Refresh the models list
      await fetchModels();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const handleAddModel = () => {
    if (activeTab === "llm") {
      setIsLLMCreateModalOpen(true);
    } else {
      setIsEmbeddingCreateModalOpen(true);
    }
  };

  const handleEditModel = (model: Model) => {
    setEditingModel(model);
    if (activeTab === "llm") {
      setIsLLMEditModalOpen(true);
    } else {
      setIsEmbeddingEditModalOpen(true);
    }
  };

  const handleLLMModelSubmit = async (data: any) => {
    try {
      // Create new model - use type field with enum key
      const modelEnum = LLM_MODEL_MAPPING[data.type];
      if (!modelEnum) {
        throw new Error(`Unknown LLM model type: ${data.type}`);
      }

      const createData: CreateModelRequest = {
        name: data.name,
        type: modelEnum, // Use the enum key (e.g., "GPT_OSS")
        model_metadata: JSON.parse(data.configuration || "{}")
      };

      if (settingsType === "global") {
        await globalModelsApi.createLLM(createData);
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        await organizationModelsApi.createLLM(organizationId, createData);
      }

      // Refresh the models list
      await fetchModels();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const handleLLMModelEdit = async (data: any) => {
    try {
      if (!editingModel) {
        throw new Error("No model selected for editing");
      }

      // Update existing model - only name and model_metadata
      const updateData: UpdateModelRequest = {
        name: data.name,
        model_metadata: JSON.parse(data.configuration || "{}")
      };

      if (settingsType === "global") {
        await globalModelsApi.updateLLM(editingModel.id, updateData);
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        await organizationModelsApi.updateLLM(organizationId, editingModel.id, updateData);
      }

      // Refresh the models list
      await fetchModels();
      setEditingModel(null);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const handleEmbeddingModelSubmit = async (data: any) => {
    try {
      // Create new model - use type field with enum key
      const modelEnum = EMBEDDING_MODEL_MAPPING[data.type];
      if (!modelEnum) {
        throw new Error(`Unknown embedding model type: ${data.type}`);
      }

      const createData: CreateModelRequest = {
        name: data.name,
        type: modelEnum, // Use the enum key (e.g., "AZURE_TEXT")
        model_metadata: JSON.parse(data.configuration || "{}")
      };

      if (settingsType === "global") {
        await globalModelsApi.createEmbedding(createData);
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        await organizationModelsApi.createEmbedding(organizationId, createData);
      }

      // Refresh the models list
      await fetchModels();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const handleEmbeddingModelEdit = async (data: any) => {
    try {
      if (!editingModel) {
        throw new Error("No model selected for editing");
      }

      // Update existing model - only name and model_metadata
      const updateData: UpdateModelRequest = {
        name: data.name,
        model_metadata: JSON.parse(data.configuration || "{}")
      };

      if (settingsType === "global") {
        await globalModelsApi.updateEmbedding(editingModel.id, updateData);
      } else {
        const organizationId = user?.current_organization?.id;
        if (!organizationId) {
          throw new Error("No organization selected");
        }
        await organizationModelsApi.updateEmbedding(organizationId, editingModel.id, updateData);
      }

      // Refresh the models list
      await fetchModels();
      setEditingModel(null);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card extra="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="border-t-transparent mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-blue-500"></div>
              <p className="text-gray-600 dark:text-gray-400">
                {t("settings.loadingModels")}
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card extra="p-6">
          <div className="py-8 text-center">
            <p className="mb-4 text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={fetchModels}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t("common.retry")}
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card extra="p-6">
        {/* Tab Navigation */}
        <div className="mb-6 flex space-x-0 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab("llm")}
            className={`rounded-t-lg px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "llm"
                ? "border-b-2 border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            {t("settings.llmModel")}
          </button>
          <button
            onClick={() => setActiveTab("embedding")}
            className={`rounded-t-lg px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === "embedding"
                ? "border-b-2 border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            {t("settings.embeddingModel")}
          </button>
        </div>

        {/* GenericTable */}
        <GenericTable
          data={models || []}
          columns={columns}
          searchPlaceholder={`${t("settings.searchModel")} ${
            activeTab === "llm" ? t("settings.llm") : t("settings.embedding")
          }`}
          onAdd={handleAddModel}
          addLabel={`${t("settings.addModel")} ${
            activeTab === "llm" ? t("settings.llm") : t("settings.embedding")
          }`}
          customQueryName={`${settingsType}_${activeTab}_search`}
        />
      </Card>

      {/* Modals */}
      <CreateLLMModelModal
        isOpen={isLLMCreateModalOpen}
        setIsOpen={setIsLLMCreateModalOpen}
        onSubmit={handleLLMModelSubmit}
        settingsType={settingsType}
      />

      <EditLLMModelModal
        isOpen={isLLMEditModalOpen}
        setIsOpen={(open) => {
          setIsLLMEditModalOpen(open);
          if (!open) setEditingModel(null);
        }}
        onSubmit={handleLLMModelEdit}
        initialData={editingModel ? {
          name: editingModel.name,
          type: getLLMDisplayName(editingModel.model, editingModel.model_metadata),
          configuration: JSON.stringify(editingModel.model_metadata, null, 2)
        } : {
          name: "",
          type: "",
          configuration: ""
        }}
        settingsType={settingsType}
      />

      <CreateEmbeddingModelModal
        isOpen={isEmbeddingCreateModalOpen}
        setIsOpen={setIsEmbeddingCreateModalOpen}
        onSubmit={handleEmbeddingModelSubmit}
        settingsType={settingsType}
      />

      <EditEmbeddingModelModal
        isOpen={isEmbeddingEditModalOpen}
        setIsOpen={(open) => {
          setIsEmbeddingEditModalOpen(open);
          if (!open) setEditingModel(null);
        }}
        onSubmit={handleEmbeddingModelEdit}
        initialData={editingModel ? {
          name: editingModel.name,
          type: getEmbeddingDisplayName(editingModel.model, editingModel.model_metadata),
          configuration: JSON.stringify(editingModel.model_metadata, null, 2)
        } : {
          name: "",
          type: "",
          configuration: ""
        }}
        settingsType={settingsType}
      />
    </div>
  );
};

export default ModelPage;
